import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { Card, Button, Text, Title, Stack, Group } from "@mantine/core";
import { Shield, MessageSquare, ArrowLeft, Check, AlertCircle } from "lucide-react";

interface Scope {
  scope: string;
  description: string;
  default: boolean;
}

const ERROR_MESSAGES: Record<string, string> = {
  access_denied: "You declined the Slack authorization. No changes were made.",
  missing_params: "The authorization response was incomplete. Please try again.",
  expired: "The authorization session expired. Please try again.",
  already_connected: "This site already has a Slack workspace connected.",
  workspace_in_use: "That Slack workspace is already connected to Nodus elsewhere.",
};

/** Installs the official Nodus Slack app: the permissions it asks for, then Slack's consent screen. */
export default function SlackInstallPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const errorParam = searchParams.get("error");
  const [installing, setInstalling] = useState(false);

  const { data: scopes = [], isLoading } = useQuery({
    queryKey: ["slack-scopes"],
    queryFn: async () => {
      const res = await api.get("/slack/scopes");
      return res.data as Scope[];
    },
  });

  const requiredScopes = scopes.filter((s) => s.default);

  const handleInstall = async () => {
    setInstalling(true);
    try {
      const res = await api.get("/tenant-portal/slack-install/");
      window.location.href = res.data.url;
    } catch {
      setInstalling(false);
    }
  };

  return (
    <Stack px="md" py="lg" gap="md">
      <Button
        variant="subtle"
        size="compact-sm"
        onClick={() => navigate("/settings/slack")}
        style={{ alignSelf: "flex-start" }}
      >
        <ArrowLeft size={16} style={{ marginRight: 4 }} /> Back
      </Button>

      {/* Error banner from OAuth redirect */}
      {errorParam && (
        <Group
          gap={8}
          align="flex-start"
          wrap="nowrap"
          p={12} fz={14}
          style={{
            borderRadius: 6,
            background: "var(--mantine-color-red-light)",
            color: "var(--mantine-color-red-filled)",
          }}
        >
          <AlertCircle size={16} style={{ marginTop: 2, flexShrink: 0 }} />
          <Text fz="sm">
            {errorParam === "already_connected" && searchParams.get("team")
              ? `This site already has a Slack workspace connected ("${searchParams.get("team")}"). Remove it first before connecting a new workspace.`
              : ERROR_MESSAGES[errorParam] || `Authorization failed: ${errorParam}`}
          </Text>
        </Group>
      )}

      <Group gap="sm">
        <Shield size={24} style={{ color: "var(--mantine-primary-color-filled)" }} />
        <div>
          <Title order={3}>Add Nodus to Slack</Title>
          <Text fz="sm" c="dimmed">Review permissions before connecting to Slack</Text>
        </div>
      </Group>

      {isLoading ? (
        <Text fz="sm" c="dimmed">Loading...</Text>
      ) : (
        <>
          <Card withBorder padding="lg">
            <Stack gap="xs" mb="sm">
              <Text fz="sm" fw={500}>Permissions</Text>
              <Text fz="xs" c="dimmed">What the Nodus app needs in your workspace</Text>
            </Stack>
            <Stack gap="xs">
              {requiredScopes.map((s) => (
                <Group key={s.scope} align="flex-start" gap="xs" py={4}>
                  <Check size={16} style={{ color: "var(--mantine-color-green-5)", marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <Text fz="sm" fw={500}>{s.scope}</Text>
                    <Text fz="xs" c="dimmed">{s.description}</Text>
                  </div>
                </Group>
              ))}
            </Stack>
          </Card>

          <Button fullWidth size="lg" onClick={handleInstall} disabled={installing} loading={installing}>
            <MessageSquare size={16} style={{ marginRight: 8 }} />
            Continue to Slack
          </Button>

          <Text fz="xs" c="dimmed" ta="center">
            You'll be redirected to Slack to authorize the app for your workspace.
          </Text>
        </>
      )}
    </Stack>
  );
}
